import { ExpandLess, ExpandMore, Search } from '@mui/icons-material';
import { Box, Collapse, MenuList, Popover, TextField, styled, useTheme } from '@mui/material';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MenuItemLink } from '~/components/link';
import { ListItemText } from '~/components/list-row';
import { useAppContext } from '~/contexts';
import { NavItemLink } from './NavItems';
import { NetworkAvatar } from './NetworkAvatar';

export const FilterContainer = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  borderBottom: '1px solid rgb(63, 63, 63, .15)',
  display: 'flex',
  padding: theme.spacing(0.75, 1),
}));

export const NetworkMenuItem = styled(MenuItemLink)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.borders.main}`,
  padding: theme.spacing(1, 2),

  '&:last-child': {
    borderBottom: '0',
  },
}));

const NetworkButtonLink = styled(NavItemLink)(({ theme }) => ({
  borderRadius: theme.spacing(0.5),
  backgroundColor: theme.palette.primary.hover,
  '& span svg': {
    color: theme.palette.common.white,
  },
  '& .MuiListItemText-root  .MuiTypography-root': {
    color: theme.palette.common.white,
  },
}));

export const NetworkButton = () => {
  const { t } = useTranslation();
  const { currentNetwork, currentUser } = useAppContext();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');

  const theme = useTheme();

  const networks = useMemo(
    () =>
      currentUser.networks.filter((network) => {
        return currentUser.admin ? network : !network.public;
      }),
    [currentUser],
  );

  const handleToggle = () => {
    if (networks.length <= 1) return;
    setOpen((prevOpen) => !prevOpen);
  };

  return (
    <Box>
      <NetworkButtonLink
        aria-expanded={networks.length > 1 ? open : undefined}
        aria-haspopup={networks.length > 1 ? 'menu' : undefined}
        ref={buttonRef}
        endIcon={networks.length > 1 ? open ? <ExpandLess /> : <ExpandMore /> : null}
        icon={<NetworkAvatar icon={currentNetwork.logoUrl} />}
        onClick={handleToggle}
        disabled={networks.length === 1}
        text={currentNetwork.name}
      />
      <Popover
        TransitionComponent={Collapse}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        marginThreshold={0}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        TransitionProps={{
          onEntered: () => inputRef.current?.focus(),
          onExited: () => setFilter(''),
        }}
        anchorEl={buttonRef.current}
        onClose={handleToggle}
        open={open}
        sx={{
          '& .MuiPaper-root': {
            overflow: 'hidden',
            width: '300px',
            border: 1,
            borderColor: 'borders.main',
            marginTop: '4px',
          },
        }}
      >
        <FilterContainer>
          <TextField
            fullWidth
            size="small"
            InputProps={{
              startAdornment: <Search fontSize="small" sx={{ mr: 1, opacity: 0.6 }} />,
            }}
            inputProps={{ 'aria-label': t('common.network_menu.filter_placeholder') }}
            inputRef={inputRef}
            onChange={(event) => setFilter(event.target.value)}
            placeholder={t('common.network_menu.filter_placeholder')}
          />
        </FilterContainer>

        <MenuList
          sx={{
            maxHeight: 500,
            overflowY: 'auto',
          }}
        >
          {networks.map((network) => {
            if (!network.name.toLocaleLowerCase().includes(filter.toLocaleLowerCase())) return null;
            return (
              <NetworkMenuItem
                disabled={network.id === currentNetwork.id}
                key={network.id}
                onClick={() => setOpen(false)}
                to={`/networks/${network.id}`}
              >
                <NetworkAvatar icon={network.logoUrl} sx={{ marginRight: theme.spacing(1) }} />
                <ListItemText>{network.name}</ListItemText>
              </NetworkMenuItem>
            );
          })}
        </MenuList>
      </Popover>
    </Box>
  );
};
